import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { FileText, HardDrive, Users, Clock, Plus, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format } from 'date-fns';
import { CartesianGrid } from "recharts";

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#6366f1'];

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="card flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  </div>
);

const AppDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/documents/dashboard').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div>;

  const { stats, recentDocs, docsByStatus, docsByType } = data || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Your document workspace overview</p>
        </div>
        <Link to="/app/documents/upload" className="btn-primary">
          <Plus className="w-4 h-4" /> Upload Document
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={FileText} label="Total Documents" value={stats?.totalDocs || 0} color="bg-primary-600" />
        <StatCard icon={HardDrive} label="Storage Used" value={`${stats?.totalStorageMb || 0} MB`} color="bg-indigo-500" />
        <StatCard icon={Users} label="Active Users" value={stats?.totalUsers || 0} color="bg-emerald-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Type */}
        <div className="card">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Documents by Type</h2>
          {docsByType?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={docsByType} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="file_extension" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="flex items-center justify-center h-48 text-gray-400 text-sm">No documents yet</div>}
        </div>

        {/* By Status */}
        <div className="card">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Documents by Status</h2>
          {docsByStatus?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={docsByStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={75}
                  label={({ status, count }) => `${status}: ${count}`}>
                  {docsByStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="flex items-center justify-center h-48 text-gray-400 text-sm">No documents yet</div>}
        </div>
      </div>

      {/* Recent Documents */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-800">Recent Documents</h2>
          <Link to="/app/documents" className="text-primary-600 text-sm hover:underline">View all</Link>
        </div>
        {recentDocs?.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p>No documents yet. <Link to="/app/documents/upload" className="text-primary-600 hover:underline">Upload your first document.</Link></p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentDocs?.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 py-3 hover:bg-gray-50 rounded-lg px-2 -mx-2 transition-colors">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/app/documents/${doc.id}`} className="font-medium text-sm text-gray-900 hover:text-primary-600 truncate block">
                    {doc.title}
                  </Link>
                  <p className="text-xs text-gray-400">by {doc.created_by_name} · {format(new Date(doc.updated_at), 'MMM d, yyyy')}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  doc.status === 'published' ? 'bg-green-100 text-green-700' :
                  doc.status === 'archived' ? 'bg-gray-100 text-gray-500' : 'bg-yellow-100 text-yellow-700'
                }`}>{doc.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppDashboard;
